import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Input,
  Label,
  Progress,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import CountUp from "react-countup";
import { toast } from "react-toastify";
import {
  getAutomatedSearch,
  startAutomatedSearch,
} from "common/services/search.service";

type EntityType = "organization" | "person" | "unknown";

type SourceStatus = "QUEUED" | "RUNNING" | "DONE" | "FAILED";

type ReportStatus =
  | "QUEUED"
  | "RUNNING"
  | "SYNTHESIZING"
  | "GENERATING_PDF"
  | "COMPLETED"
  | "FAILED";

interface SanctionMatch {
  source: string;
  listType: string;
  matchedName: string;
  score: number;
  band: string;
}

interface CimsMatch {
  entityType: string;
  matchedName: string;
  score: number;
  flagged: boolean;
  flaggedStatus: string;
  details?: string;
  link?: string;
}

interface Finding {
  category: string;
  severity: string;
  title: string;
  detail: string;
  sourceUrls: string[];
}

interface ContractSummary {
  totalContracts: number;
  totalValue: number;
  averageValue: number;
  minValue: number;
  maxValue: number;
  firstStart?: string;
  lastEnd?: string;
  ltaCount: number;
  byAgency: { agency: string; count: number; value: number }[];
}

interface DirectorScreening {
  name: string;
  role?: string;
  origin: string;
  results: { source: string; label: string; match: boolean; score?: number }[];
  cimsFlag?: string;
  anyMatch: boolean;
}

interface CimsProfile {
  found: boolean;
  contractorName?: string;
  website?: string;
  financial?: ContractSummary;
  agencies: string[];
  associatedEntities: { name: string; relationship?: string; source?: string }[];
  associatedIndividuals: { name: string; role?: string; source?: string }[];
  directorScreenings: DirectorScreening[];
}

interface AutomatedReport {
  id: number;
  query: string;
  entityType: EntityType;
  country?: string;
  status: ReportStatus;
  riskRating?: string;
  riskScore?: number;
  assessment?: {
    executiveSummary: string;
    findings: Finding[];
    recommendation: string;
    confidence: string;
    dataGaps: string[];
  };
  sanctions?: { matches: SanctionMatch[]; hit: boolean };
  cimsMatches?: { matches: CimsMatch[]; hit: boolean };
  cimsProfile?: CimsProfile;
  progress?: Record<string, { status: SourceStatus; summary?: string }>;
  error?: string;
  reportFileUrl?: string | null;
  reportFileError?: string | null;
}

const SOURCE_ORDER: { key: string; label: string; icon: string }[] = [
  { key: "SANCTIONS", label: "Sanctions & Watchlists", icon: "ri-shield-check-line" },
  { key: "CIMS_INTERNAL", label: "CIMS Internal Records", icon: "ri-database-2-line" },
  { key: "WEB", label: "Web Search", icon: "ri-global-line" },
  { key: "NEWS", label: "News & Media", icon: "ri-newspaper-line" },
  { key: "LINKEDIN", label: "LinkedIn", icon: "ri-linkedin-box-line" },
  { key: "SOCIAL", label: "Social Media", icon: "ri-chat-3-line" },
];

const ACTIVE_STATUSES: ReportStatus[] = [
  "QUEUED",
  "RUNNING",
  "SYNTHESIZING",
  "GENERATING_PDF",
];

// Predefined countries. The full country name is used end-to-end (form, API,
// and report); the backend maps it to a region hint internally. "Unknown"
// means no country is applied.
const COUNTRY_OPTIONS: { label: string; value: string }[] = [
  { label: "Unknown", value: "" },
  { label: "Kenya", value: "Kenya" },
  { label: "Somalia", value: "Somalia" },
  { label: "Afghanistan", value: "Afghanistan" },
  { label: "South Sudan", value: "South Sudan" },
  { label: "Ethiopia", value: "Ethiopia" },
];

const riskColor = (rating?: string): string => {
  switch (rating) {
    case "LOW":
      return "success";
    case "MEDIUM":
      return "warning";
    case "HIGH":
      return "danger";
    case "CRITICAL":
      return "danger";
    default:
      return "secondary";
  }
};

// Hex values (aligned with the Velzon palette) for the SVG risk dial.
const riskHex = (rating?: string): string => {
  switch (rating) {
    case "LOW":
      return "#0ab39c";
    case "MEDIUM":
      return "#f7b84b";
    case "HIGH":
      return "#f06548";
    case "CRITICAL":
      return "#f06548";
    default:
      return "#878a99";
  }
};

const severityColor = (severity: string): string => {
  switch (severity) {
    case "critical":
      return "dark";
    case "high":
      return "danger";
    case "medium":
      return "warning";
    case "low":
      return "info";
    default:
      return "secondary";
  }
};

const severityHex = (severity: string): string => {
  switch (severity) {
    case "critical":
      return "#212529";
    case "high":
      return "#f06548";
    case "medium":
      return "#f7b84b";
    case "low":
      return "#299cdb";
    default:
      return "#878a99";
  }
};

const phaseLabel = (status?: ReportStatus): string => {
  switch (status) {
    case "QUEUED":
      return "Queued";
    case "RUNNING":
      return "Collecting information";
    case "SYNTHESIZING":
      return "Synthesizing risk assessment";
    case "GENERATING_PDF":
      return "Generating report";
    default:
      return "Processing";
  }
};

const statusMeta = (
  status?: ReportStatus
): { color: string; label: string } => {
  switch (status) {
    case "COMPLETED":
      return { color: "success", label: "Completed" };
    case "FAILED":
      return { color: "danger", label: "Failed" };
    case "QUEUED":
      return { color: "secondary", label: "Queued" };
    default:
      return { color: "primary", label: "In progress" };
  }
};

/** Card with a consistent icon + title header used across the report. */
const SectionCard = ({
  icon,
  tone = "primary",
  title,
  subtitle,
  action,
  children,
}: {
  icon: string;
  tone?: string;
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <Card className="shadow-none border">
    <CardHeader className="d-flex align-items-center border-bottom-dashed">
      <div className="avatar-xs flex-shrink-0 me-2">
        <span className={`avatar-title rounded fs-16 bg-${tone}-subtle text-${tone}`}>
          <i className={icon}></i>
        </span>
      </div>
      <div className="flex-grow-1">
        <h6 className="card-title mb-0 fs-15">{title}</h6>
        {subtitle && <small className="text-muted">{subtitle}</small>}
      </div>
      {action && <div className="flex-shrink-0">{action}</div>}
    </CardHeader>
    <CardBody>{children}</CardBody>
  </Card>
);

/** Donut-style score indicator coloured by risk rating. */
const ScoreDial = ({ score, rating }: { score: number; rating?: string }) => {
  const size = 128;
  const stroke = 12;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.max(0, Math.min(100, score));
  const dash = (clamped / 100) * circumference;
  const color = riskHex(rating);
  return (
    <div
      className="position-relative flex-shrink-0"
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#eff2f7"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circumference}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className="position-absolute top-50 start-50 translate-middle text-center">
        <span className="d-block fw-bold ff-secondary" style={{ fontSize: 30, color }}>
          {clamped}
        </span>
        <span className="text-muted fs-11">out of 100</span>
      </div>
    </div>
  );
};

/** Velzon-style KPI card: icon avatar beside a prominent, aligned figure. */
const StatCard = ({
  label,
  value,
  icon,
  tone = "primary",
  prefix,
}: {
  label: string;
  value: number | string;
  icon: string;
  tone?: string;
  prefix?: string;
}) => (
  <Col xl={3} sm={6}>
    <Card className="card-animate shadow-none border mb-0 h-100">
      <CardBody>
        <div className="d-flex align-items-center">
          <div className="avatar-sm flex-shrink-0">
            <span className={`avatar-title rounded fs-3 bg-${tone}-subtle text-${tone}`}>
              <i className={icon}></i>
            </span>
          </div>
          <div className="flex-grow-1 ms-3 overflow-hidden">
            <p className="text-uppercase fw-medium text-muted text-truncate mb-1 fs-12">
              {label}
            </p>
            <h3 className="mb-0 ff-secondary fw-semibold">
              {typeof value === "number" ? (
                <CountUp
                  start={0}
                  end={value}
                  separator=","
                  prefix={prefix}
                  duration={1.5}
                />
              ) : (
                value
              )}
            </h3>
          </div>
        </div>
      </CardBody>
    </Card>
  </Col>
);

const SourceProgress = ({
  status,
  summary,
  label,
  icon,
  last,
}: {
  status: SourceStatus;
  summary?: string;
  label: string;
  icon: string;
  last?: boolean;
}) => {
  let tone = "secondary";
  let badge: React.ReactNode = (
    <span className="badge bg-secondary-subtle text-secondary">Queued</span>
  );
  let indicator: React.ReactNode = (
    <span className="avatar-title rounded-circle bg-light text-muted">
      <i className={icon}></i>
    </span>
  );
  switch (status) {
    case "DONE":
      tone = "success";
      badge = <span className="badge bg-success-subtle text-success">Done</span>;
      indicator = (
        <span className="avatar-title rounded-circle bg-success-subtle text-success">
          <i className="ri-check-line"></i>
        </span>
      );
      break;
    case "RUNNING":
      tone = "primary";
      badge = <span className="badge bg-primary-subtle text-primary">Running</span>;
      indicator = (
        <span className="avatar-title rounded-circle bg-primary-subtle text-primary">
          <Spinner size="sm" color="primary" />
        </span>
      );
      break;
    case "FAILED":
      tone = "danger";
      badge = <span className="badge bg-danger-subtle text-danger">Failed</span>;
      indicator = (
        <span className="avatar-title rounded-circle bg-danger-subtle text-danger">
          <i className="ri-close-line"></i>
        </span>
      );
      break;
    default:
      break;
  }

  return (
    <div
      className={
        "d-flex align-items-center py-3" + (last ? "" : " border-bottom border-bottom-dashed")
      }
    >
      <div className="avatar-xs flex-shrink-0 me-3">{indicator}</div>
      <div className="flex-grow-1">
        <h6 className="mb-0 fs-14">{label}</h6>
        {summary && <small className="text-muted">{summary}</small>}
      </div>
      <div className={`flex-shrink-0 text-${tone}`}>{badge}</div>
    </div>
  );
};

const AutomatedSearch = () => {
  const [query, setQuery] = useState("");
  const [entityType, setEntityType] = useState<EntityType>("organization");
  const [country, setCountry] = useState("");
  const [starting, setStarting] = useState(false);
  const [report, setReport] = useState<AutomatedReport | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const poll = useCallback(
    async (id: number) => {
      try {
        const response: any = await getAutomatedSearch(id);
        const next: AutomatedReport = response?.report;
        if (next) {
          setReport(next);
          if (!ACTIVE_STATUSES.includes(next.status)) {
            stopPolling();
          }
        }
      } catch {
        // transient error; keep polling
      }
    },
    [stopPolling]
  );

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim().length < 2) {
      toast.error("Please enter at least 2 characters.");
      return;
    }
    setStarting(true);
    try {
      const response: any = await startAutomatedSearch({
        query: query.trim(),
        entityType,
        country: country.trim() || undefined,
      });
      const id = response?.id;
      if (!id) {
        throw new Error("No report id returned");
      }
      setReport({
        id,
        query: query.trim(),
        entityType,
        country: country.trim(),
        status: "QUEUED",
      });
      stopPolling();
      pollRef.current = setInterval(() => poll(id), 3000);
      poll(id);
    } catch {
      toast.error("Failed to start the automated due diligence search.");
    } finally {
      setStarting(false);
    }
  };

  const resetSearch = () => {
    stopPolling();
    setReport(null);
    setQuery("");
    setCountry("");
    setEntityType("organization");
  };

  // ----- Search form -----
  if (!report) {
    return (
      <Card className="overflow-hidden">
        <div
          className="p-4 p-lg-5 text-center position-relative"
          style={{
            background:
              "linear-gradient(135deg, rgba(64,81,137,0.12) 0%, rgba(10,179,156,0.10) 100%)",
          }}
        >
          <div className="avatar-md mx-auto mb-3">
            <div className="avatar-title bg-primary text-white rounded-circle fs-32 shadow">
              <i className="ri-sparkling-2-line text-white fs-24"></i>
            </div>
          </div>
          <h4 className="mb-2 fw-semibold">Automated Due Diligence</h4>
          <p className="text-muted fs-15 mb-0 mx-auto lh-base" style={{ maxWidth: 640 }}>
            Screen an organization or individual across sanctions, watchlists, and
            internal CIMS records, enrich with web, news, and social intelligence,
            then generate an AI-assisted Partner Verification Report.
          </p>
          <div className="d-flex flex-wrap justify-content-center gap-2 mt-3">
            {SOURCE_ORDER.map((s) => (
              <span
                key={s.key}
                className="badge bg-white text-body border fw-medium fs-12 py-2 px-3"
              >
                <i className={`${s.icon} align-bottom me-1 text-primary`}></i>
                {s.label}
              </span>
            ))}
          </div>
        </div>

        <CardBody className="p-4 p-lg-5">
          <form onSubmit={onSubmit}>
            <Row className="justify-content-center">
              <Col lg={9}>
                <Row className="g-3">
                  <Col xs={12}>
                    <Label htmlFor="dd-query" className="form-label fs-14 fw-medium">
                      Name of entity or person
                    </Label>
                    <div className="position-relative">
                      <Input
                        id="dd-query"
                        type="text"
                        bsSize="lg"
                        className="ps-5"
                        placeholder="e.g. Acme Trading LLC or Jane Doe"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        autoFocus
                      />
                      <i className="ri-search-2-line position-absolute top-50 translate-middle-y text-muted fs-18 ms-3 start-0"></i>
                    </div>
                  </Col>
                  <Col md={6}>
                    <Label htmlFor="dd-type" className="form-label fs-14 fw-medium">
                      Entity type
                    </Label>
                    <Input
                      id="dd-type"
                      type="select"
                      bsSize="lg"
                      value={entityType}
                      onChange={(e) => setEntityType(e.target.value as EntityType)}
                    >
                      <option value="organization">Organization</option>
                      <option value="person">Person</option>
                      <option value="unknown">Unknown</option>
                    </Input>
                  </Col>
                  <Col md={6}>
                    <Label htmlFor="dd-country" className="form-label fs-14 fw-medium">
                      Country
                    </Label>
                    <Input
                      id="dd-country"
                      type="select"
                      bsSize="lg"
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                    >
                      {COUNTRY_OPTIONS.map((c) => (
                        <option key={c.label} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </Input>
                  </Col>
                </Row>
                <div className="text-center mt-4">
                  <Button
                    color="primary"
                    size="lg"
                    type="submit"
                    className="px-4"
                    disabled={starting || query.trim().length < 2}
                  >
                    {starting ? (
                      <Spinner size="sm" className="me-2" />
                    ) : (
                      <i className="ri-sparkling-line me-1"></i>
                    )}
                    Run Due Diligence
                  </Button>
                  <p className="text-muted fs-13 mt-2 mb-0">
                    Typically completes in under a minute.
                  </p>
                </div>
              </Col>
            </Row>
          </form>
        </CardBody>
      </Card>
    );
  }

  const isActive = ACTIVE_STATUSES.includes(report.status);
  const isFailed = report.status === "FAILED";
  const isDone = report.status === "COMPLETED";

  const progressPct =
    report.status === "QUEUED"
      ? 5
      : report.status === "RUNNING"
        ? 45
        : report.status === "SYNTHESIZING"
          ? 75
          : report.status === "GENERATING_PDF"
            ? 90
            : 100;

  const status = statusMeta(report.status);

  return (
    <>
      {/* Header */}
      <Card className="shadow-none border">
        <CardBody>
          <div className="d-flex align-items-center flex-wrap gap-3">
            <div className="avatar-md flex-shrink-0">
              <span className="avatar-title bg-primary-subtle text-primary rounded fs-28 text-uppercase">
                {report.query?.charAt(0) || (
                  <i className="ri-building-line"></i>
                )}
              </span>
            </div>
            <div className="flex-grow-1">
              <h4 className="mb-1">{report.query}</h4>
              <div className="d-flex align-items-center flex-wrap gap-2">
                <span className="badge bg-light text-body border text-capitalize">
                  <i className="ri-price-tag-3-line align-bottom me-1"></i>
                  {report.entityType}
                </span>
                {report.country && (
                  <span className="badge bg-light text-body border">
                    <i className="ri-map-pin-line align-bottom me-1"></i>
                    {report.country}
                  </span>
                )}
                <span className={`badge bg-${status.color}-subtle text-${status.color}`}>
                  {isActive && (
                    <Spinner size="sm" style={{ width: 8, height: 8 }} className="me-1" />
                  )}
                  {status.label}
                </span>
              </div>
            </div>
            <div className="flex-shrink-0 hstack gap-2">
              {isDone && report.reportFileUrl && (
                <a
                  className="btn btn-primary"
                  href={report.reportFileUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  <i className="ri-download-2-line align-bottom me-1"></i>
                  Download PVR
                </a>
              )}
              {isDone && !report.reportFileUrl && report.reportFileError && (
                <span
                  className="badge bg-warning-subtle text-warning fs-13 px-3 py-2"
                  title={report.reportFileError}
                >
                  <i className="ri-error-warning-line align-bottom me-1"></i>
                  PDF unavailable
                </span>
              )}
              <Button color="light" onClick={resetSearch}>
                <i className="ri-restart-line align-bottom me-1"></i>
                New Search
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Progress */}
      {(isActive || isFailed) && (
        <SectionCard
          icon="ri-loader-4-line"
          tone={isFailed ? "danger" : "primary"}
          title={isFailed ? "Assessment failed" : "Assessment in progress"}
          subtitle={
            isActive ? phaseLabel(report.status) : undefined
          }
        >
          {isActive && (
            <div className="mb-4">
              <div className="d-flex justify-content-between mb-2">
                <span className="fw-medium text-muted">
                  {phaseLabel(report.status)}
                </span>
                <span className="fw-semibold text-primary">{progressPct}%</span>
              </div>
              <Progress
                animated
                value={progressPct}
                color="primary"
                style={{ height: 8 }}
              />
            </div>
          )}
          {isFailed && (
            <Alert color="danger" className="mb-4">
              <strong>The assessment could not be completed. </strong>
              {report.error || "An unexpected error occurred."}
            </Alert>
          )}
          <div>
            {SOURCE_ORDER.map((s, idx) => {
              const p = report.progress?.[s.key];
              return (
                <SourceProgress
                  key={s.key}
                  label={s.label}
                  icon={s.icon}
                  status={p?.status || "QUEUED"}
                  summary={p?.summary}
                  last={idx === SOURCE_ORDER.length - 1}
                />
              );
            })}
          </div>
        </SectionCard>
      )}

      {/* Results */}
      {isDone && report.assessment && (
        <>
          {!report.reportFileUrl && report.reportFileError && (
            <Alert color="warning" className="d-flex align-items-start gap-2 mb-4">
              <i className="ri-error-warning-line fs-18 flex-shrink-0"></i>
              <div>
                <strong>The PDF report could not be generated.</strong>{" "}
                {report.reportFileError}
              </div>
            </Alert>
          )}

          {/* Risk overview hero */}
          <Card className="shadow-none border">
            <CardBody className="p-4">
              <Row className="align-items-center g-4">
                <Col xs="auto" className="mx-auto mx-sm-0">
                  <ScoreDial
                    score={report.riskScore ?? 0}
                    rating={report.riskRating}
                  />
                </Col>
                <Col>
                  <span
                    className={`badge bg-${riskColor(
                      report.riskRating
                    )}-subtle text-${riskColor(report.riskRating)} fs-13 mb-2`}
                  >
                    {report.riskRating || "UNKNOWN"} RISK
                  </span>
                  <h4 className="mb-1">Overall Risk Rating</h4>
                  <p className="text-muted mb-0 fs-14">
                    Confidence in this assessment is
                    <span className="fw-medium text-body text-capitalize">
                      {" "}
                      {report.assessment.confidence}
                    </span>
                    . Score reflects the weighted severity of all findings across
                    screened sources.
                  </p>
                </Col>
              </Row>
            </CardBody>
          </Card>

          {/* Key figures */}
          <Row className="g-3 mb-4">
            <StatCard
              label="Sanctions Hits"
              value={report.sanctions?.matches.length ?? 0}
              icon="ri-shield-check-line"
              tone="danger"
            />
            <StatCard
              label="CIMS Matches"
              value={report.cimsMatches?.matches.length ?? 0}
              icon="ri-database-2-line"
              tone="info"
            />
            <StatCard
              label="CIMS Contracts"
              value={report.cimsProfile?.financial?.totalContracts ?? 0}
              icon="ri-file-list-3-line"
              tone="primary"
            />
            <StatCard
              label="Findings"
              value={report.assessment.findings.length}
              icon="ri-search-eye-line"
              tone="warning"
            />
          </Row>

          {/* Executive summary */}
          <SectionCard icon="ri-file-text-line" title="Executive Summary">
            <p className="text-body mb-0 fs-15 lh-base">
              {report.assessment.executiveSummary}
            </p>
          </SectionCard>

          {/* Sanctions */}
          <SectionCard
            icon="ri-shield-check-line"
            tone="danger"
            title="Sanctions & Watchlist Screening"
            action={
              report.sanctions && report.sanctions.matches.length > 0 ? (
                <span className="badge bg-danger-subtle text-danger">
                  {report.sanctions.matches.length} match
                  {report.sanctions.matches.length === 1 ? "" : "es"}
                </span>
              ) : (
                <span className="badge bg-success-subtle text-success">Clear</span>
              )
            }
          >
            {report.sanctions && report.sanctions.matches.length > 0 ? (
              <div className="table-responsive">
                <Table className="align-middle table-nowrap table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Matched Name</th>
                      <th>Source</th>
                      <th>List</th>
                      <th>Score</th>
                      <th>Band</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.sanctions.matches.map((m, i) => (
                      <tr key={i}>
                        <td className="fw-medium">{m.matchedName}</td>
                        <td>{m.source}</td>
                        <td>{m.listType}</td>
                        <td>
                          <span
                            className={
                              "badge bg-" +
                              (m.band === "escalate"
                                ? "danger"
                                : m.band === "review"
                                  ? "warning"
                                  : "secondary") +
                              "-subtle text-" +
                              (m.band === "escalate"
                                ? "danger"
                                : m.band === "review"
                                  ? "warning"
                                  : "secondary")
                            }
                          >
                            {m.score}
                          </span>
                        </td>
                        <td className="text-capitalize">
                          {m.band.replace(/_/g, " ")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : (
              <div className="text-center text-muted py-3">
                <i className="ri-checkbox-circle-line text-success fs-32 d-block mb-2"></i>
                No sanctions or watchlist matches above the review threshold.
              </div>
            )}
          </SectionCard>

          {/* CIMS internal */}
          <SectionCard
            icon="ri-database-2-line"
            tone="info"
            title="CIMS Internal Records"
            action={
              report.cimsMatches && report.cimsMatches.matches.length > 0 ? (
                <span className="badge bg-info-subtle text-info">
                  {report.cimsMatches.matches.length} record
                  {report.cimsMatches.matches.length === 1 ? "" : "s"}
                </span>
              ) : undefined
            }
          >
            {report.cimsMatches && report.cimsMatches.matches.length > 0 ? (
              <div className="table-responsive">
                <Table className="align-middle table-nowrap table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Matched Record</th>
                      <th>Type</th>
                      <th>CIMS Status</th>
                      <th>Score</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.cimsMatches.matches.map((m, i) => (
                      <tr key={i}>
                        <td>
                          <span className="fw-medium">{m.matchedName}</span>
                          {m.details && (
                            <div>
                              <small className="text-muted">{m.details}</small>
                            </div>
                          )}
                        </td>
                        <td className="text-capitalize">{m.entityType}</td>
                        <td>
                          <span
                            className={
                              "badge bg-" +
                              (m.flagged ? "danger" : "secondary") +
                              "-subtle text-" +
                              (m.flagged ? "danger" : "secondary")
                            }
                          >
                            {m.flaggedStatus}
                          </span>
                        </td>
                        <td>{m.score}</td>
                        <td>
                          {m.link && (
                            <a
                              className="link-primary fw-medium"
                              href={m.link}
                              target="_blank"
                              rel="noreferrer"
                            >
                              View{" "}
                              <i className="ri-arrow-right-up-line align-bottom"></i>
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : (
              <div className="text-center text-muted py-3">
                <i className="ri-inbox-line fs-32 d-block mb-2"></i>
                No matching contractor or person records were found in CIMS.
              </div>
            )}
          </SectionCard>

          {/* Financial overview */}
          {report.cimsProfile?.financial &&
            report.cimsProfile.financial.totalContracts > 0 && (
              <SectionCard
                icon="ri-money-dollar-circle-line"
                tone="success"
                title="Financial Overview"
                subtitle="Based on contract history in CIMS"
              >
                <Row className="g-3 mb-4">
                  <StatCard
                    label="Contracts Awarded"
                    value={report.cimsProfile.financial.totalContracts}
                    icon="ri-file-list-3-line"
                    tone="primary"
                  />
                  <StatCard
                    label="Total Value"
                    value={Math.round(report.cimsProfile.financial.totalValue)}
                    icon="ri-funds-line"
                    tone="success"
                  />
                  <StatCard
                    label="Average Value"
                    value={Math.round(
                      report.cimsProfile.financial.averageValue
                    )}
                    icon="ri-bar-chart-2-line"
                    tone="info"
                  />
                  <StatCard
                    label="Long-term Agreements"
                    value={report.cimsProfile.financial.ltaCount}
                    icon="ri-links-line"
                    tone="warning"
                  />
                </Row>
                <h6 className="text-muted text-uppercase fs-12 mb-2">
                  By Awarding Agency
                </h6>
                <div className="table-responsive">
                  <Table className="align-middle table-nowrap table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Awarding Agency</th>
                        <th className="text-end">Contracts</th>
                        <th className="text-end">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.cimsProfile.financial.byAgency.map((a, i) => (
                        <tr key={i}>
                          <td className="fw-medium">{a.agency}</td>
                          <td className="text-end">{a.count}</td>
                          <td className="text-end">
                            {Math.round(a.value).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </SectionCard>
            )}

          {/* Associated entities & individuals */}
          {report.cimsProfile &&
            (report.cimsProfile.associatedEntities.length > 0 ||
              report.cimsProfile.directorScreenings.length > 0) && (
              <SectionCard
                icon="ri-team-line"
                tone="primary"
                title="Associated Entities & Individuals"
              >
                {report.cimsProfile.associatedEntities.length > 0 && (
                  <div className="mb-4">
                    <h6 className="text-muted text-uppercase fs-12 mb-2">
                      Associated Entities
                    </h6>
                    <div className="table-responsive">
                      <Table className="align-middle table-nowrap table-hover mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Entity</th>
                            <th>Relationship</th>
                            <th>Source</th>
                          </tr>
                        </thead>
                        <tbody>
                          {report.cimsProfile.associatedEntities.map((e, i) => (
                            <tr key={i}>
                              <td className="fw-medium">{e.name}</td>
                              <td>{e.relationship || "Not specified"}</td>
                              <td>
                                <span className="badge bg-light text-body border">
                                  {e.source || "CIMS"}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                )}

                {report.cimsProfile.directorScreenings.length > 0 && (
                  <div>
                    <h6 className="text-muted text-uppercase fs-12 mb-2">
                      Associated Individuals (Screened)
                    </h6>
                    <div className="table-responsive">
                      <Table className="align-middle table-nowrap table-hover mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Full Name</th>
                            <th>Role</th>
                            <th>UNSC</th>
                            <th>OFAC</th>
                            <th>EU</th>
                            <th>UK</th>
                            <th>UNGM / WB</th>
                            <th>CIMS</th>
                          </tr>
                        </thead>
                        <tbody>
                          {report.cimsProfile.directorScreenings.map((s, i) => (
                            <tr key={i}>
                              <td className="fw-medium">{s.name}</td>
                              <td>{s.role || "-"}</td>
                              {[0, 1, 2, 3, 4].map((idx) => (
                                <td key={idx}>
                                  {s.results[idx]?.match ? (
                                    <span className="badge bg-warning-subtle text-warning">
                                      {s.results[idx]?.score ?? "Hit"}
                                    </span>
                                  ) : (
                                    <span className="text-muted fs-12">
                                      No match
                                    </span>
                                  )}
                                </td>
                              ))}
                              <td>
                                {s.cimsFlag && s.cimsFlag !== "None" ? (
                                  <span className="badge bg-danger-subtle text-danger">
                                    {s.cimsFlag}
                                  </span>
                                ) : (
                                  <span className="text-muted fs-12">None</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                )}
              </SectionCard>
            )}

          {/* Findings */}
          <SectionCard
            icon="ri-search-eye-line"
            tone="warning"
            title="Findings"
            action={
              report.assessment.findings.length > 0 ? (
                <span className="badge bg-warning-subtle text-warning">
                  {report.assessment.findings.length}
                </span>
              ) : undefined
            }
          >
            {report.assessment.findings.length === 0 ? (
              <div className="text-center text-muted py-3">
                <i className="ri-checkbox-circle-line text-success fs-32 d-block mb-2"></i>
                No specific findings recorded.
              </div>
            ) : (
              <div className="vstack gap-3">
                {report.assessment.findings.map((f, i) => (
                  <div
                    key={i}
                    className="border rounded p-3 border-start border-3"
                    style={{ borderLeftColor: severityHex(f.severity) }}
                  >
                    <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
                      <span
                        className={`badge bg-${severityColor(
                          f.severity
                        )}-subtle text-${severityColor(
                          f.severity
                        )} text-capitalize`}
                      >
                        {f.severity}
                      </span>
                      <span className="badge bg-light text-body border text-capitalize">
                        {f.category.replace(/_/g, " ")}
                      </span>
                      <strong className="fs-15">{f.title}</strong>
                    </div>
                    <p className="text-muted mb-1 fs-14 lh-base">{f.detail}</p>
                    {f.sourceUrls.length > 0 && (
                      <div className="d-flex flex-column gap-1 mt-2">
                        {f.sourceUrls.map((u, j) => (
                          <a
                            key={j}
                            className="link-secondary text-truncate fs-13"
                            href={u}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <i className="ri-external-link-line align-bottom me-1"></i>
                            {u}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </SectionCard>

          {/* Recommendation */}
          <SectionCard
            icon="ri-lightbulb-flash-line"
            tone="success"
            title="Recommendation"
          >
            <div className="d-flex bg-success-subtle rounded p-3">
              <i className="ri-double-quotes-l text-success fs-24 me-2 flex-shrink-0"></i>
              <p className="text-body mb-0 fs-15 lh-base">
                {report.assessment.recommendation}
              </p>
            </div>
          </SectionCard>

          {/* Data gaps */}
          {report.assessment.dataGaps.length > 0 && (
            <SectionCard
              icon="ri-error-warning-line"
              tone="secondary"
              title="Data Gaps & Limitations"
            >
              <ul className="vstack gap-2 text-muted mb-0 fs-14 ps-0 list-unstyled">
                {report.assessment.dataGaps.map((g, i) => (
                  <li key={i} className="d-flex">
                    <i className="ri-arrow-right-s-line text-secondary flex-shrink-0 me-1 fs-18"></i>
                    <span>{g}</span>
                  </li>
                ))}
              </ul>
            </SectionCard>
          )}
        </>
      )}
    </>
  );
};

export default AutomatedSearch;
